import { useState } from 'react';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { MessagesSquare } from 'lucide-react';
import { useMessageThreadQuery } from '@/client/api/messaging/messaging.query';
import {
  AdminMessage,
  formatEpoch,
  inquiryVisitorName,
  isInquiryMessage,
  MessageThreadParams,
  STATUS_BADGE,
} from '@/client/types/messaging';
import {
  MessageParties,
  ThreadPartyLabel,
} from '@/client/components/admin/Messaging/PartyHoverCard';

interface PartyThreadDrawerProps {
  params: MessageThreadParams | null;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  onClose: () => void;
  onSelectMessage?: (ref: string) => void;
}

const MessageBubble = ({
  msg,
  onSelect,
}: {
  msg: AdminMessage;
  onSelect?: (ref: string) => void;
}) => {
  const inquiry = isInquiryMessage(msg);
  return (
    <button
      type="button"
      className={`w-full rounded-lg border p-3 text-left ${
        msg.is_deleted
          ? 'border-red-100 bg-red-50/40'
          : inquiry
            ? 'border-amber-100 bg-amber-50/50'
            : 'border-gray-100 bg-white'
      } hover:border-blue-200`}
      onClick={() => onSelect?.(msg.id || msg.hash || '')}
    >
      <div className="mb-1 flex flex-wrap items-center gap-2">
        <MessageParties msg={msg} className="text-xs" />
        {inquiry && (
          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800">
            Inquiry
          </span>
        )}
        <span
          className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${
            STATUS_BADGE[msg.status ?? 'sent'] ?? STATUS_BADGE.sent
          }`}
        >
          {msg.status ?? 'sent'}
        </span>
        <span className="text-[11px] text-gray-400">
          {formatEpoch(msg.timestamp ?? msg.created_at)}
        </span>
      </div>
      <p className="whitespace-pre-wrap text-sm text-gray-900">
        {msg.message || (msg.redacted ? '[redacted]' : '—')}
      </p>
      {inquiry && (
        <div className="mt-2 space-y-0.5 text-xs text-amber-900">
          {inquiryVisitorName(msg) && <p>Visitor: {inquiryVisitorName(msg)}</p>}
          {msg.attachment?.visitor_email && <p>Email: {msg.attachment.visitor_email}</p>}
          {msg.attachment?.visitor_phone && <p>Phone: {msg.attachment.visitor_phone}</p>}
          <p className="italic text-amber-700">
            Display-only — reply via email/phone, not in-thread.
          </p>
        </div>
      )}
    </button>
  );
};

/** Thread viewer via GET /messages/thread (party-based; works without conversation_key). */
const PartyThreadDrawer = ({
  params,
  isOpen,
  setIsOpen,
  onClose,
  onSelectMessage,
}: PartyThreadDrawerProps) => {
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const queryParams =
    isOpen && params
      ? {
          ...params,
          include_deleted: includeDeleted ? 'true' : 'false',
          limit: 100,
        }
      : null;
  const { data, isLoading } = useMessageThreadQuery(queryParams, isOpen && !!params);

  if (!isOpen) return null;

  const messages = data?.messages ?? [];

  return (
    <Drawer open={isOpen} onOpenChange={setIsOpen}>
      <DrawerContent className="mx-auto flex max-h-[92vh] max-w-3xl flex-col">
        <DrawerHeader className="border-b border-gray-100 text-left">
          <DrawerTitle className="flex items-center gap-2">
            <MessagesSquare className="h-5 w-5 text-blue-600" />
            {isLoading ? (
              'Loading…'
            ) : data?.party_a || data?.party_b ? (
              <span className="inline-flex flex-wrap items-center gap-1">
                <ThreadPartyLabel party={data?.party_a} />
                <span className="text-gray-400">↔</span>
                <ThreadPartyLabel party={data?.party_b} />
              </span>
            ) : (
              'Party thread'
            )}
          </DrawerTitle>
          <DrawerDescription>
            Full back-and-forth via parties
            {data?.entity != null ? ` · entity #${data.entity}` : ''}
            {' '}(works when conversation_key is null)
          </DrawerDescription>
        </DrawerHeader>

        <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-500">
              {data?.pagination?.total != null
                ? `${data.pagination.total} message${data.pagination.total === 1 ? '' : 's'}`
                : ''}
            </p>
            <label className="flex items-center gap-2 text-xs text-gray-600">
              <input
                type="checkbox"
                checked={includeDeleted}
                onChange={(e) => setIncludeDeleted(e.target.checked)}
              />
              Include deleted
            </label>
          </div>

          {isLoading &&
            Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
          {!isLoading && messages.length === 0 && (
            <p className="py-8 text-center text-sm text-gray-500">No messages between these parties</p>
          )}
          {!isLoading &&
            messages.map((msg) => (
              <MessageBubble
                key={msg.id || msg.hash}
                msg={msg}
                onSelect={onSelectMessage}
              />
            ))}
        </div>

        <DrawerFooter className="border-t border-gray-100">
          <DrawerClose asChild>
            <Button type="button" variant="ghost" size="sm" onClick={onClose}>
              Close
            </Button>
          </DrawerClose>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default PartyThreadDrawer;
